import { useMutation, useQueryClient } from '@tanstack/react-query';
import { iemService } from '@/services/iem';
import type {
  AllocationSizeConversionRequest,
  AllocationSizeConversionResponse,
  AllocationSizeConversionBatchRequest,
  AllocationSizeConversionBatchResponse,
  ThrConversionRequest,
  ThrConversionResponse,
  ThrDueDateUpdateRequest,
  ThrDueDateUpdateResponse,
} from '@/services/iem';
import { useNotifications } from '@/stores/useNotificationStore';
import { getEligibilityReason, isCustomerEligibilityError } from '@/utils/iemEligibility';
import { getErrorMessage } from '@/utils/httpClient';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'useIEMMutations' });

const formatDisplayDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

// IEM related mutation hooks (write/trigger operations)

export const useCustomerLookup = () => {
  const notifications = useNotifications();
  return useMutation({
    mutationFn: (badgeId: string) => iemService.lookupCustomer(badgeId),
    onError: (error: unknown) => {
      log.error('Customer lookup failed', { error: getErrorMessage(error) });
      const message = getErrorMessage(error);
      if (isCustomerEligibilityError(error)) {
        const reason = getEligibilityReason(error);
        notifications.error('Customer Not Eligible', reason ?? message);
        return;
      }
      notifications.error('Customer Lookup Failed', message);
    },
  });
};

export const useBadgeLookup = () => {
  const notifications = useNotifications();
  return useMutation({
    mutationFn: (individualId: string) => iemService.lookupByIndividualId(individualId),
    onError: (error: unknown) => {
      log.error('Badge scan lookup failed', { error: getErrorMessage(error) });
      const message = getErrorMessage(error);
      if (isCustomerEligibilityError(error)) {
        const reason = getEligibilityReason(error);
        notifications.error('Customer Not Eligible', reason ?? message);
        return;
      }
      notifications.error('Badge Scan Failed', message);
    },
  });
};

export const useExtendThrDueDate = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<ThrDueDateUpdateResponse, unknown, ThrDueDateUpdateRequest>({
    mutationFn: (input: ThrDueDateUpdateRequest) => iemService.extendThrDueDate(input),
    onSuccess: (result) => {
      notifications.success(
        'THR Due Date Updated',
        `Due date now ${formatDisplayDate(result.newDueDate)}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['thr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['phr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
    },
    onError: (error: unknown) => {
      notifications.error('Extend THR Due Date Failed', getErrorMessage(error));
    },
  });
};

export const useConvertThrToPhr = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<ThrConversionResponse, unknown, ThrConversionRequest>({
    mutationFn: (input: ThrConversionRequest) => iemService.convertThrToPhr(input),
    onSuccess: () => {
      notifications.success('THR Converted', 'Temporary hand receipt converted to permanent.');
      void queryClient.invalidateQueries({ queryKey: ['thr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['phr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
    },
    onError: (error: unknown) => {
      notifications.error('Convert THR Failed', getErrorMessage(error));
    },
  });
};

export const useConvertAllocationToSizedAsset = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<AllocationSizeConversionResponse, unknown, AllocationSizeConversionRequest>({
    mutationFn: (input: AllocationSizeConversionRequest) =>
      iemService.convertAllocationToSizedAsset(input),
    onSuccess: () => {
      notifications.success('Allocation Updated', 'Allocation converted to the selected size.');
      void queryClient.invalidateQueries({ queryKey: ['thr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['phr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
    },
    onError: (error: unknown) => {
      notifications.error('Convert to Sized Asset Failed', getErrorMessage(error));
    },
  });
};

export const useConvertAllocationToSizedAssetBatch = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<AllocationSizeConversionBatchResponse, unknown, AllocationSizeConversionBatchRequest>({
    mutationFn: (input: AllocationSizeConversionBatchRequest) =>
      iemService.convertAllocationToSizedAssetBatch(input),
    onSuccess: (_data) => {
      notifications.success('Allocation Updated', 'Allocations converted to the selected size.');
      void queryClient.invalidateQueries({ queryKey: ['thr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['phr-items'] });
      void queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
    },
    onError: (error: unknown) => {
      notifications.error('Convert to Sized Asset Failed', getErrorMessage(error));
    },
  });
};
